import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  SimpleGrid,
  HStack,
  VStack,
  Text,
  Button,
  Badge,
  Input,
  InputGroup,
  InputLeftElement,
  Select,
  Table,
  TableContainer,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Flex,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  IconButton,
  Icon,
  Spinner,
  Tooltip,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { useNavigate } from "react-router-dom";
import { MdMoreVert, MdVisibility, MdEdit, MdSearch } from "react-icons/md";
import MissionStatCard from "components/card/MissionStatCard";
import { useNavbarAction } from "contexts/NavbarActionContext";
import apiClient from "../../../api/client";
import { BASE_URL } from "config/api";
import AffectationModalPanel from "./AffectationModalPanel";
import { loadAffectationResources } from "./loadAffectationResources";
import { fetchVehicleLocation as fetchVehicleLocationSecure } from "../../../api/external";
import { handleDossierCreemissionSideEffects } from "../missions/utils/missionCreationSideEffects";
import { io } from "socket.io-client";

const getSocketServerUrl = () => {
  try {
    const parsed = new URL(BASE_URL);
    return parsed.origin;
  } catch (_) {
    return BASE_URL;
  }
};

export default function DossierRegulation() {
  const navigate = useNavigate();
  const { showStats } = useNavbarAction();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [activeTab, setActiveTab] = useState("Tous");
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("Tous statuts");
  const [sortBy, setSortBy] = useState("Derniere");
  const [dossiers, setDossiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedDossier, setSelectedDossier] = useState(null);
  const [assigningMission, setAssigningMission] = useState(false);
  const [vehicles, setVehicles] = useState([]);
  const [allAmbulanciers, setAllAmbulanciers] = useState([]);
  const [isReloadingAffectation, setIsReloadingAffectation] = useState(false);

  const refetchDossiers = useCallback(
    async (silent = false) => {
      try {
        if (!silent) setLoading(true);
        const dossierRes = await apiClient.get(`${BASE_URL}/dossier`);

        const dossierList = Array.isArray(dossierRes.data) ? dossierRes.data : [];

        setDossiers(dossierList);
      } catch (e) {
        setError("Impossible de charger les dossiers.");
      } finally {
        if (!silent) setLoading(false);
      }
    },
    []
  );

  useEffect(() => {
    refetchDossiers(false);
  }, [refetchDossiers]);

  useEffect(() => {
    const socket = io(getSocketServerUrl(), {
      withCredentials: true,
      transports: ["websocket"],
    });

    socket.on("connect", () => {
      socket.emit("join_dossiers");
      socket.emit("join_room", "dossiers");
      socket.emit("join_room", { room: "dossiers" });
    });

    socket.on("dossier:created", () => {
      refetchDossiers(true);
    });

    socket.on("dossier:updated", () => {
      refetchDossiers(true);
    });

    socket.on("dossier:decision-updated", () => {
      refetchDossiers(true);
    });

    socket.on("dossier:deleted", () => {
      refetchDossiers(true);
    });

    return () => {
      socket.disconnect();
    };
  }, [refetchDossiers]);

  const formatSexe = (sexe) => {
    const s = String(sexe || "").trim().toLowerCase();
    if (["m", "male", "homme", "h"].includes(s)) return "Homme";
    if (["f", "female", "femme"].includes(s)) return "Femme";
    return sexe || "—";
  };

  const truncateText = (value, maxLength = 15) => {
    const text = String(value || "");
    if (text.length <= maxLength) return text;
    return `${text.slice(0, maxLength)}...`;
  };

  const shouldTruncate = (value, maxLength = 15) => String(value || "").length > maxLength;

  const normalizedDossiers = useMemo(() => {
    return dossiers.map((dossier) => ({
      patientData: dossier.patient || null,
      id: dossier.id,
      reference: `DR-${String(dossier.id || "").padStart(4, "0")}`,
      patientName: (() => {
        const patient = dossier.patient;
        if (!patient) return dossier.patient_id ? `Patient #${dossier.patient_id}` : "---";
        return `${patient.nom || ""} ${patient.prenom || ""}`.trim() || `Patient #${dossier.patient_id}`;
      })(),
      patientMeta: (() => {
        const patient = dossier.patient;
        if (!patient) return "";
        const age = patient.age != null && String(patient.age).trim() !== "" ? String(patient.age) : "—";
        return `${age} · ${formatSexe(patient.sexe)}`;
      })(),
      ipp: (() => {
        const patient = dossier.patient;
        if (!patient) return "---";
        return patient.ipp != null && String(patient.ipp).trim() !== "" ? String(patient.ipp) : "---";
      })(),
      urgence: dossier.cause ? String(dossier.cause).toUpperCase() : "---",
      statutDossier: dossier.statut || "---",
      statutMission: dossier.mission_statut || "---",
      missionId: dossier.mission_id || dossier.missionId || null,
      adresseMission: dossier.adresse || "---",
      hopitalDestination: dossier.adresse_destination || "---",
      loc: dossier.loc || null,
      locDest: dossier.loc_dest || null,
      armCreateur: dossier.created_by_full_name
        ? dossier.created_by_full_name
        : dossier.créé_par
        ? `Utilisateur #${dossier.créé_par}`
        : "---",
      medecinAssigne: dossier.assigne
        ? `${dossier.assigne_full_name || "Utilisateur"}`
        : "---",
    }));
  }, [dossiers]);

  const tabLabels = [
    "Tous",
    "Urgents",
    "En attente medecin",
    "A affecter",
    "Termine",
  ];

  const normalizeStatut = (statut) =>
    String(statut || "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");

  const isUrgenceCritique = (urgence) => {
    const u = String(urgence || "").toLowerCase();
    return u.includes("u0") || u.includes("u1");
  };

  const tabCounts = useMemo(() => {
    return {
      Tous: normalizedDossiers.length,
      Urgents: normalizedDossiers.filter((dossier) => isUrgenceCritique(dossier.urgence)).length,
      "En attente medecin": normalizedDossiers.filter(
        (dossier) => normalizeStatut(dossier.statutDossier) === "attente medecin"
      ).length,
      "A affecter": normalizedDossiers.filter(
        (dossier) => normalizeStatut(dossier.statutDossier) === "attente affectation"
      ).length,
      Termine: normalizedDossiers.filter(
        (dossier) => normalizeStatut(dossier.statutDossier) === "termine"
      ).length,
    };
  }, [normalizedDossiers]);

  const dossierStats = useMemo(() => {
    const enCours = normalizedDossiers.filter(
      (dossier) => normalizeStatut(dossier.statutDossier) !== "termine"
    ).length;
    const urgencesCritiques = normalizedDossiers.filter((dossier) =>
      isUrgenceCritique(dossier.urgence)
    ).length;
    const attenteMedecin = normalizedDossiers.filter(
      (dossier) => normalizeStatut(dossier.statutDossier) === "attente medecin"
    ).length;

    return { enCours, urgencesCritiques, attenteMedecin };
  }, [normalizedDossiers]);

  const filteredDossiers = useMemo(() => {
    const search = searchQuery.trim().toLowerCase();

    const filtered = normalizedDossiers.filter((dossier) => {
      const matchTab =
        activeTab === "Tous" ||
        (activeTab === "Urgents" &&
          (dossier.urgence.toLowerCase().includes("u0") ||
            dossier.urgence.toLowerCase().includes("u1"))) ||
        (activeTab === "En attente medecin" &&
          dossier.statutDossier.toLowerCase() === "attente medecin") ||
        (activeTab === "A affecter" &&
          dossier.statutDossier.toLowerCase() === "attente affectation") ||
        (activeTab === "Termine" && dossier.statutDossier.toLowerCase() === "termine");

      const matchStatusFilter =
        statusFilter === "Tous statuts" ||
        dossier.statutDossier.toLowerCase() === statusFilter.toLowerCase();

      if (!search) return matchTab && matchStatusFilter;

      const matchSearch =
        dossier.reference.toLowerCase().includes(search) ||
        dossier.patientName.toLowerCase().includes(search) ||
        dossier.patientMeta.toLowerCase().includes(search) ||
        dossier.ipp.toLowerCase().includes(search) ||
        dossier.adresseMission.toLowerCase().includes(search) ||
        dossier.hopitalDestination.toLowerCase().includes(search) ||
        dossier.armCreateur.toLowerCase().includes(search) ||
        dossier.medecinAssigne.toLowerCase().includes(search);

      return matchTab && matchStatusFilter && matchSearch;
    });

    return [...filtered].sort((a, b) => {
      if (sortBy === "Ancienne") return a.id - b.id;
      return b.id - a.id;
    });
  }, [activeTab, searchQuery, statusFilter, sortBy, normalizedDossiers]);

  const isMissionCreatedStatus = (status) => {
    const normalized = String(status || "")
      .trim()
      .toLowerCase()
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "");
    return normalized === "mission creee";
  };

  const parseLoc = (value) => {
    if (!value) return null;
    if (typeof value === "object" && value.lat && value.lng) {
      return { lat: Number(value.lat), lng: Number(value.lng) };
    }
    const raw = String(value);
    const [latStr, lngStr] = raw.split(",");
    const lat = Number(latStr);
    const lng = Number(lngStr);
    if (Number.isNaN(lat) || Number.isNaN(lng)) return null;
    return { lat, lng };
  };

  const missionLocation = useMemo(() => {
    return parseLoc(selectedDossier?.loc) || parseLoc(selectedDossier?.locDest);
  }, [selectedDossier]);

  const applyAffectationResources = async ({ showSuccessToast = false } = {}) => {
    const { vehicles: nextVehicles, ambulanciers } = await loadAffectationResources();
    setVehicles(nextVehicles);
    setAllAmbulanciers(ambulanciers);
    if (showSuccessToast) {
      toast({
        title: "Liste mise à jour",
        description: `${nextVehicles.length} ambulance(s) disponible(s), ${ambulanciers.length} ambulancier(s).`,
        status: "success",
        duration: 3000,
        isClosable: true,
      });
    }
    return { vehicles: nextVehicles, ambulanciers };
  };

  const handleOpenAffectation = async (dossier) => {
    setSelectedDossier(dossier);
    onOpen();
    try {
      await applyAffectationResources();
    } catch (e) {
      setVehicles([]);
      setAllAmbulanciers([]);
      toast({
        title: "Erreur",
        description: "Impossible de charger les données d'affectation.",
        status: "error",
        isClosable: true,
      });
    }
  };

  const reloadAffectationData = async () => {
    try {
      setIsReloadingAffectation(true);
      await applyAffectationResources({ showSuccessToast: true });
    } catch (e) {
      toast({
        title: "Erreur",
        description: "Impossible de recharger les ambulances et ambulanciers.",
        status: "error",
        isClosable: true,
      });
    } finally {
      setIsReloadingAffectation(false);
    }
  };

  const handleCloseAffectation = () => {
    setAssigningMission(false);
    setSelectedDossier(null);
    onClose();
  };

  const handleConfirmAffectation = async ({ vehicle, selectedDoctorId, selectedNurseId, commentaire }) => {
    if (!selectedDossier?.id) return;
    if (!vehicle?.id || !vehicle?.user_id) {
      toast({
        title: "Sélection requise",
        description: "Veuillez sélectionner une ambulance avec ambulancier assigné.",
        status: "warning",
        isClosable: true,
      });
      return;
    }

    try {
      setAssigningMission(true);
      const creemissionPayload = {
        ambulance_id: Number(vehicle.id),
        ambulancier_id: Number(vehicle.user_id),
      };
      if (selectedDoctorId) {
        creemissionPayload.doctor_id = Number(selectedDoctorId);
      }
      if (selectedNurseId) {
        creemissionPayload.nurse_id = Number(selectedNurseId);
      }
      if (commentaire) {
        creemissionPayload.commentaire = commentaire;
      }
      const creemissionRes = await apiClient.post(
        `${BASE_URL}/dossier/${selectedDossier.id}/creemission`,
        creemissionPayload
      );

      const rawDossier = dossiers.find((d) => String(d.id) === String(selectedDossier.id)) || null;
      await handleDossierCreemissionSideEffects({
        creemissionResponse: creemissionRes,
        dossier: rawDossier,
        affectationPayload: creemissionPayload,
        vehicle,
        patient: rawDossier?.patient || selectedDossier.patientData || null,
      });

      toast({
        title: "Mission créée",
        description: "La mission a été créée avec succès.",
        status: "success",
        isClosable: true,
      });

      handleCloseAffectation();

      // Rafraîchir la liste
      const dossierRes = await apiClient.get(`${BASE_URL}/dossier`);
      setDossiers(Array.isArray(dossierRes.data) ? dossierRes.data : []);
    } catch (e) {
      toast({
        title: "Erreur",
        description: "Impossible de créer la mission depuis ce dossier.",
        status: "error",
        isClosable: true,
      });
    } finally {
      setAssigningMission(false);
    }
  };

  const fetchVehicleLocation = async (trackerId) => {
    if (!trackerId) return null;
    try {
      return await fetchVehicleLocationSecure(trackerId);
    } catch (e) {
      return null;
    }
  };

  return (
    <Box pt={{ base: "200px", md: "160px", xl: "160px" }}>
      {showStats && (
        <SimpleGrid mb="20px" columns={{ base: 1, md: 2, lg: 3 }} spacing="10px">
          <MissionStatCard
            title="Total Dossiers en cours"
            value={dossierStats.enCours}
            isPositive={true}
            iconColor="#7239EA"
            subtitle="hors dossiers termines"
          />
          <MissionStatCard
            title="Urgences critiques (u0-u1)"
            value={dossierStats.urgencesCritiques}
            isPositive={false}
            iconColor="#F8285A"
            subtitle="sur l'ensemble des dossiers"
          />
          <MissionStatCard
            title="Dossiers en attente medecin"
            value={dossierStats.attenteMedecin}
            isPositive={true}
            iconColor="#29BACD"
            subtitle="statut attente medecin"
          />
        </SimpleGrid>
      )}

      <Box bg="white" borderRadius="12px" border="1px solid #E5E5E5" p="16px">
        <VStack align="stretch" spacing="14px">
          <HStack justify="space-between" wrap="wrap" spacing="10px">
            <HStack
              spacing="2px"
              bg="#F3F4F6"
              border="1px solid #E5E5E5"
              borderRadius="10px"
              p="4px"
              overflowX="auto"
            >
              {tabLabels.map((tab) => {
                const isActive = activeTab === tab;
                return (
                  <Button
                    key={tab}
                    size="sm"
                    h="30px"
                    px="10px"
                    borderRadius="999px"
                    bg={isActive ? "#2B6CF0" : "transparent"}
                    color={isActive ? "white" : "#111827"}
                    fontSize="13px"
                    fontWeight={isActive ? "600" : "500"}
                    _hover={{ bg: isActive ? "#2B6CF0" : "#EDEFF2" }}
                    onClick={() => setActiveTab(tab)}
                  >
                    <HStack spacing="6px">
                      <Text>{tab}</Text>
                      <Text
                        fontSize="12px"
                        fontWeight="600"
                        color={isActive ? "white" : "#111827"}
                        opacity={isActive ? 1 : 0.85}
                      >
                        {tabCounts[tab]}
                      </Text>
                    </HStack>
                  </Button>
                );
              })}
            </HStack>

            <HStack>
              <InputGroup size="sm" maxW="260px">
                <InputLeftElement pointerEvents="none">
                  <Icon as={MdSearch} color="gray.400" />
                </InputLeftElement>
                <Input
                  placeholder="Recherche d'un dossier..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </InputGroup>
              <Select
                size="sm"
                maxW="150px"
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
              >
                <option>Tous statuts</option>
                <option>attente medecin</option>
                <option>Mission creee</option>
                <option>Attente affectation</option>
                <option>Termine</option>
              </Select>
              <Select
                size="sm"
                maxW="120px"
                value={sortBy}
                onChange={(e) => setSortBy(e.target.value)}
              >
                <option>Derniere</option>
                <option>Ancienne</option>
              </Select>
              
            </HStack>
          </HStack>

          {loading ? (
            <Flex align="center" justify="center" minH="220px">
              <Spinner />
            </Flex>
          ) : error ? (
            <Flex align="center" justify="center" minH="220px">
              <Text color="red.500">{error}</Text>
            </Flex>
          ) : (
          <Box border="1px solid" borderColor="gray.200" borderRadius="12px" overflow="hidden">
            <TableContainer>
              <Table
                size="sm"
                minW="1400px"
                sx={{
                  "th, td": {
                    borderRight: "1px solid",
                    borderBottom: "1px solid",
                    borderColor: "gray.200",
                    whiteSpace: "nowrap",
                  },
                  "th:last-child, td:last-child": { borderRight: "none" },
                  "tr:last-child td": { borderBottom: "none" },
                  "thead tr": { bg: "gray.50" },
                }}
              >
                <Thead bg="gray.50">
                  <Tr>
                    <Th px="20px" h="50px">N DOSSIER</Th>
                    <Th px="20px" h="50px">PATIENT</Th>
                    <Th px="20px" h="50px">IPP</Th>
                    <Th px="20px" h="50px">URGENCE</Th>
                    <Th px="20px" h="50px">STATUT DOSSIER</Th>
                    <Th px="20px" h="50px">STATUT MISSION</Th>
                    <Th px="20px" h="50px">ADRESSE MISSION</Th>
                    <Th px="20px" h="50px">HOPITAL DESTINATION</Th>
                    <Th px="20px" h="50px">ARM CREATEUR</Th>
                    <Th px="20px" h="50px">MEDECIN ASSIGNE</Th>
                    <Th px="20px" h="50px" textAlign="center">ACTIONS</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {filteredDossiers.map((dossier) => (
                    <Tr key={dossier.id} _hover={{ bg: "gray.50" }}>
                      <Td px="20px" h="60px">{dossier.reference.replace("DR-", "")}</Td>
                      <Td px="20px" h="60px" whiteSpace="normal">
                        <VStack align="start" spacing="0">
                          <Text fontSize="14px" fontWeight="600" color="#111827">
                            <Tooltip
                              label={dossier.patientName}
                              isDisabled={!shouldTruncate(dossier.patientName)}
                              hasArrow
                              placement="top-start"
                            >
                              <Box as="span">{truncateText(dossier.patientName)}</Box>
                            </Tooltip>
                          </Text>
                          {dossier.patientMeta ? (
                            <Text fontSize="12px" color="gray.500">
                              <Tooltip
                                label={dossier.patientMeta}
                                isDisabled={!shouldTruncate(dossier.patientMeta)}
                                hasArrow
                                placement="top-start"
                              >
                                <Box as="span">{truncateText(dossier.patientMeta)}</Box>
                              </Tooltip>
                            </Text>
                          ) : null}
                        </VStack>
                      </Td>
                      <Td px="20px" h="60px">{dossier.ipp}</Td>
                      <Td px="20px" h="60px">
                        <Badge colorScheme={dossier.urgence.toLowerCase().includes("u0") || dossier.urgence.toLowerCase().includes("u1") ? "red" : "orange"}>
                          {dossier.urgence}
                        </Badge>
                      </Td>
                      <Td px="20px" h="60px">
                        <Badge
                          colorScheme={
                            dossier.statutDossier.toLowerCase() === "attente medecin"
                              ? "orange"
                              : dossier.statutDossier.toLowerCase() === "mission creee"
                                ? "green"
                                : dossier.statutDossier.toLowerCase() === "termine"
                                  ? "blue"
                                  : "purple"
                          }
                        >
                          {dossier.statutDossier}
                        </Badge>
                      </Td>
                      <Td px="20px" h="60px">{dossier.statutMission}</Td>
                      <Td px="20px" h="60px">
                        <Tooltip
                          label={dossier.adresseMission}
                          isDisabled={!shouldTruncate(dossier.adresseMission)}
                          hasArrow
                          placement="top-start"
                        >
                          <Box as="span">{truncateText(dossier.adresseMission)}</Box>
                        </Tooltip>
                      </Td>
                      <Td px="20px" h="60px">
                        <Tooltip
                          label={dossier.hopitalDestination}
                          isDisabled={!shouldTruncate(dossier.hopitalDestination)}
                          hasArrow
                          placement="top-start"
                        >
                          <Box as="span">{truncateText(dossier.hopitalDestination)}</Box>
                        </Tooltip>
                      </Td>
                      <Td px="20px" h="60px">{dossier.armCreateur}</Td>
                      <Td px="20px" h="60px">{dossier.medecinAssigne}</Td>
                      <Td px="20px" h="60px" textAlign="center">
                        <Menu placement="left-start">
                          <MenuButton
                            as={IconButton}
                            icon={<Icon as={MdMoreVert} />}
                            aria-label="Actions"
                            size="sm"
                            variant="ghost"
                          />
                          <MenuList minW="180px">
                            {String(dossier.statutDossier || "").toLowerCase() === "attente affectation" && (
                              <MenuItem
                                icon={<Icon as={MdEdit} />}
                                onClick={() => handleOpenAffectation(dossier)}
                              >
                                Affecter
                              </MenuItem>
                            )}
                            <MenuItem
                              icon={<Icon as={MdVisibility} />}
                              onClick={() => navigate(`/admin/dossier-regulation/${dossier.id}`)}
                            >
                              Voir details
                            </MenuItem>
                            {isMissionCreatedStatus(dossier.statutDossier) && dossier.missionId ? (
                              <MenuItem
                                icon={<Icon as={MdVisibility} />}
                                onClick={() => navigate(`/admin/missions/${dossier.missionId}`)}
                              >
                                Voir mission
                              </MenuItem>
                            ) : null}
                            <MenuItem
                              icon={<Icon as={MdEdit} />}
                              onClick={() => navigate(`/admin/dossier-regulation/edit/${dossier.id}`)}
                            >
                              Modifier
                            </MenuItem>
                          </MenuList>
                        </Menu>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </Box>
          )}

          <Flex justify="space-between" align="center" px="4px" pb="4px">
            <HStack spacing="10px">
              <Text fontSize="14px" color="#455879">Afficher</Text>
              <Select size="sm" w="70px" value={20}>
                <option value={20}>20</option>
              </Select>
              <Text fontSize="14px" color="#455879">par page</Text>
            </HStack>
            <Text fontSize="14px" color="#455879">
              {filteredDossiers.length > 0 ? `1 à ${filteredDossiers.length} sur ${filteredDossiers.length}` : "0 résultat"}
            </Text>
          </Flex>
        </VStack>
      </Box>

      <Modal isOpen={isOpen} onClose={handleCloseAffectation} size="6xl" isCentered>
        <ModalOverlay />
        <ModalContent maxW="1200px">
          <ModalHeader>
            Affecter la mission - Dossier #{selectedDossier?.id || "-"}
          </ModalHeader>
          <ModalCloseButton />
          {isOpen && (
            <AffectationModalPanel
              key={selectedDossier?.id || "dossier-affectation"}
              vehicles={vehicles}
              missionLocation={missionLocation}
              ambulanciers={allAmbulanciers}
              fetchVehicleLocation={fetchVehicleLocation}
              isSubmitting={assigningMission}
              isReloading={isReloadingAffectation}
              onReload={reloadAffectationData}
              onCancel={handleCloseAffectation}
              onConfirm={handleConfirmAffectation}
              confirmLabel="Créer la mission"
            />
          )}
        </ModalContent>
      </Modal>
    </Box>
  );
}
